// =====================================================================
// Land Estate directory - BuildPilot
//
// Twelve master-planned residential estates around Adelaide that
// BuildPilot covers in the navigation/discovery storefront.
//
// IMPORTANT EDITORIAL RULES (must be honoured on any update):
//   • NEVER name unpartnered builders (e.g. Metricon, Fairmont, etc.).
//     Estates themselves are vendors - naming the estate / developer
//     is fine. Listing competitor builders is not.
//   • NEVER show estate phone, email, sales contact or a "visit the
//     vendor" link. All CTAs must route to BuildPilot's One on One
//     Support.
//   • NEVER use the word "Free" alongside advisory/support.
//   • Data is curated by hand from publicly-available sources, refreshed
//     quarterly. No live scraping.
// =====================================================================

export const estateData = {
  riverlea: {
    slug: 'riverlea',
    name: 'Riverlea',
    suburb: 'Buckland Park',
    locality: 'Buckland Park',
    region: 'Northern Adelaide',
    state: 'SA',
    developer: 'Walker Corporation',
    metaTitle: 'Riverlea Estate Buckland Park - Independent Buyer Guide | BuildPilot',
    metaDescription: 'Independent buyer guide to Riverlea at Buckland Park. Lot sizes, masterplan progress, parklands, future town centre and what to weigh up before committing. BuildPilot One on One Support available.',
    heroEyebrow: 'Northern Adelaide · Master-planned',
    heroTitle: 'Riverlea',
    heroSubtitle: "Walker Corporation's 8,000+ home community at Buckland Park",
    overview: "Riverlea is one of South Australia's largest master-planned communities - a long-horizon Walker Corporation project at Buckland Park, roughly 35 minutes north of the Adelaide CBD. Stages are rolling out alongside a future town centre, wetlands and substantial parkland, with land titling and turnkey packages both available.",
    estimatedLotSize: '300 - 600 m²',
    driveToCBD: '~35 mins',
    stage: 'Actively selling',
    estDelivery: 'Long-horizon (2024 - 2040+)',
    features: [
      'Master-planned community for 8,000+ homes at full build-out',
      'Future town centre, schools and community precinct',
      'Extensive parklands, wetlands and walking trails',
      'Multiple display villages active across stages',
      'Direct access to Port Wakefield Road',
      'Mix of titled and registering allotments',
    ],
    considerations: [
      'Estate is still developing - proximity to completed amenities varies stage to stage',
      'Each stage may carry different design covenants and landscaping timeframes',
      'Site costs vary noticeably between lot orientation and street frontage',
      'Confirm school zoning for your target stage before you commit',
      'Public transport is limited - factor commute time and second-vehicle costs',
    ],
    intentTags: ['House and land Buckland Park', 'Northern Adelaide estates', 'Adelaide master-planned community'],
    nearbySuburbs: ['Angle Vale', 'Two Wells', 'Virginia'],
  },

  eyre: {
    slug: 'eyre',
    name: 'Eyre',
    suburb: 'Penfield',
    locality: 'Penfield',
    region: 'Northern Adelaide',
    state: 'SA',
    developer: 'Lanser',
    metaTitle: 'Eyre Estate Penfield - Independent Buyer Guide | BuildPilot',
    metaDescription: 'Independent buyer guide to Eyre at Penfield. Lot sizes, masterplan, parkland, education precinct and what to consider. BuildPilot One on One Support available.',
    heroEyebrow: 'Northern Adelaide · Master-planned',
    heroTitle: 'Eyre',
    heroSubtitle: "Lanser's growing northern community at Penfield",
    overview: 'Eyre is a long-running Lanser development at Penfield, sitting between Munno Para and Angle Vale. Stages have been releasing steadily for several years and the estate is well known for its mix of compact courtyard lots and larger family-sized blocks, parkland reserves and an early-childhood / primary education precinct.',
    estimatedLotSize: '300 - 700 m²',
    driveToCBD: '~35 mins',
    stage: 'Actively selling',
    estDelivery: 'Multiple stages 2024 - 2030',
    features: [
      'Mix of compact and family-sized lots across releases',
      'Reserves, playgrounds and pedestrian links',
      'Education precinct anchored in the masterplan',
      'Walkable to local convenience retail',
      'Close to Northern Expressway / Port Wakefield Road',
    ],
    considerations: [
      'Lot orientation matters - confirm solar access on east-west streets',
      'Some stages carry strict design and landscaping covenants',
      'Site cost varies with cut/fill and retaining requirements',
      'Education precinct catchment may shift between releases',
    ],
    intentTags: ['House and land Penfield', 'Eyre estate Adelaide', 'Northern Adelaide growth'],
    nearbySuburbs: ['Munno Para', 'Angle Vale', 'Andrews Farm'],
  },

  springwood: {
    slug: 'springwood',
    name: 'Springwood',
    suburb: 'Gawler East',
    locality: 'Gawler East',
    region: 'Northern Adelaide',
    state: 'SA',
    developer: 'Springwood Communities',
    metaTitle: 'Springwood Estate Gawler East - Independent Buyer Guide | BuildPilot',
    metaDescription: "Independent buyer guide to Springwood at Gawler East. Lot sizes, hills views, schools, train access. BuildPilot's One on One Support available.",
    heroEyebrow: 'Northern Adelaide · Established',
    heroTitle: 'Springwood',
    heroSubtitle: 'Established Gawler East community with hills outlook',
    overview: "Springwood is one of Gawler East's most established residential estates, sitting on rising ground with outlooks toward the Adelaide Hills. The estate has matured over multiple stages, with treed reserves, walking links and proximity to Gawler's town centre and Adelaide-bound train line.",
    estimatedLotSize: '400 - 700 m²',
    driveToCBD: '~45 mins',
    stage: 'Later releases / select titled lots',
    estDelivery: 'Final stages 2024 - 2028',
    features: [
      'Established streetscapes with mature landscaping',
      'Views toward the Adelaide Hills from elevated lots',
      'Walking distance to local schools and reserves',
      'Train access to the Adelaide CBD via the Gawler line',
      'Quick access to the Barossa Valley',
    ],
    considerations: [
      'Premium pricing reflects established amenity and views',
      'Some later-stage lots have meaningful slope - budget for retaining and cut/fill',
      'Confirm stage-specific covenants and design guidelines',
      'Train timing and station distance vary by stage',
    ],
    intentTags: ['House and land Gawler East', 'Springwood estate', 'Hills view land Adelaide'],
    nearbySuburbs: ['Gawler', 'Willaston', 'Evanston'],
  },

  'st-andrews': {
    slug: 'st-andrews',
    name: 'St Andrews',
    suburb: 'Hewett',
    locality: 'Hewett',
    region: 'Northern Adelaide',
    state: 'SA',
    developer: 'Wel.Co',
    metaTitle: 'St Andrews Estate Hewett - Independent Buyer Guide | BuildPilot',
    metaDescription: 'Independent buyer guide to St Andrews at Hewett, north of Gawler. Lot sizes, golf-side outlook, parklands. BuildPilot One on One Support available.',
    heroEyebrow: 'Northern Adelaide · Master-planned',
    heroTitle: 'St Andrews',
    heroSubtitle: 'Hewett community alongside the Sandy Creek golf course',
    overview: 'St Andrews is a Wel.Co master-planned community at Hewett, immediately north of Gawler. It is best known for its golf-course frontage, generous parklands and a mix of lot sizes that suits both first-home buyers and downsizers wanting room for a single-storey home.',
    estimatedLotSize: '350 - 750 m²',
    driveToCBD: '~50 mins',
    stage: 'Actively selling',
    estDelivery: 'Rolling releases 2024 - 2030',
    features: [
      'Frontage to the Sandy Creek Golf Club',
      'Generous parklands and pedestrian links',
      'Mix of compact and family-sized lots',
      'Quick access to Gawler town centre and Main North Road',
      'Quiet outer-northern setting',
    ],
    considerations: [
      'Longer commute to the CBD - best for hybrid / remote workers',
      'Golf-frontage lots command a meaningful premium',
      'Confirm stage-by-stage design guidelines and landscaping requirements',
      'Public transport options are limited compared to inner Gawler',
    ],
    intentTags: ['House and land Hewett', 'St Andrews estate Gawler', 'Gawler new home estates'],
    nearbySuburbs: ['Gawler', 'Willaston', 'Evanston'],
  },

  'prospect-corner': {
    slug: 'prospect-corner',
    name: 'Prospect Corner',
    suburb: 'Prospect',
    locality: 'Prospect',
    region: 'Inner-North Adelaide',
    state: 'SA',
    developer: 'Renewal SA',
    metaTitle: 'Prospect Corner - Inner-North Adelaide Buyer Guide | BuildPilot',
    metaDescription: 'Independent buyer guide to Prospect Corner - a Renewal SA infill release in inner-north Adelaide. Lot sizes, walkable lifestyle, amenities. BuildPilot One on One Support available.',
    heroEyebrow: 'Inner-North Adelaide · Infill',
    heroTitle: 'Prospect Corner',
    heroSubtitle: 'Renewal SA infill release in walkable inner Prospect',
    overview: 'Prospect Corner is a Renewal SA infill development on a strategically located inner-north site in Prospect, around 5 km from the Adelaide CBD. It targets buyers who want a new-build home in an established walkable suburb - close to Prospect Road, cafés, schools and city-bound transport.',
    estimatedLotSize: '180 - 350 m² (compact urban)',
    driveToCBD: '~12 mins',
    stage: 'Releasing in stages',
    estDelivery: '2024 - 2027',
    features: [
      'Inner-north location around 5 km from the CBD',
      'Walkable to Prospect Road retail and dining',
      'Established schools and community amenities nearby',
      'New-build lots in an otherwise mature suburb',
      'City-bound bus and tram corridors within easy reach',
    ],
    considerations: [
      'Compact lots - most homes are single-front or duplex-style',
      'Site costs in established suburbs can include service relocation',
      'Heritage and character overlays may apply in nearby streets',
      'Premium land pricing reflects inner-north demand',
    ],
    intentTags: ['Prospect land for sale', 'Inner north Adelaide infill', 'Renewal SA Prospect'],
    nearbySuburbs: ['Nailsworth', 'Sefton Park', 'Broadview'],
  },

  newenham: {
    slug: 'newenham',
    name: 'Newenham',
    suburb: 'Mount Barker',
    locality: 'Mount Barker',
    region: 'Adelaide Hills',
    state: 'SA',
    developer: 'Burke Urban',
    metaTitle: 'Newenham Estate Mount Barker - Independent Buyer Guide | BuildPilot',
    metaDescription: 'Independent buyer guide to Newenham at Mount Barker. Hills lifestyle, parkland, design guidelines. BuildPilot One on One Support available.',
    heroEyebrow: 'Adelaide Hills · Master-planned',
    heroTitle: 'Newenham',
    heroSubtitle: 'Burke Urban hills community at Mount Barker',
    overview: 'Newenham is a Burke Urban master-planned community at Mount Barker, designed around extensive open space, native landscaping and a strong set of architectural design guidelines. It is one of the Hills estates known for above-average streetscape consistency and ongoing community programming.',
    estimatedLotSize: '350 - 800 m²',
    driveToCBD: '~35 mins',
    stage: 'Actively selling',
    estDelivery: 'Rolling releases 2024 - 2030',
    features: [
      'Adelaide Hills lifestyle with cooler micro-climate',
      'Strong architectural and landscaping guidelines',
      'Extensive open space and trails',
      'Walkable to Mount Barker amenity precincts',
      'Community events and resident programming',
    ],
    considerations: [
      'Design guidelines materially affect façade and material choices',
      'Hills slope can drive site cost - confirm before signing',
      'Bushfire (BAL) overlays may apply on some lots',
      'Mount Barker freeway access can congest at peak times',
    ],
    intentTags: ['House and land Mount Barker', 'Newenham estate', 'Adelaide Hills new homes'],
    nearbySuburbs: ['Littlehampton', 'Nairne', 'Hahndorf'],
  },

  larkview: {
    slug: 'larkview',
    name: 'Larkview',
    suburb: 'Mount Barker',
    locality: 'Mount Barker',
    region: 'Adelaide Hills',
    state: 'SA',
    developer: 'Hickinbotham',
    metaTitle: 'Larkview Estate Mount Barker - Independent Buyer Guide | BuildPilot',
    metaDescription: 'Independent buyer guide to Larkview at Mount Barker. Lot sizes, hills setting, family amenities. BuildPilot One on One Support available.',
    heroEyebrow: 'Adelaide Hills · Master-planned',
    heroTitle: 'Larkview',
    heroSubtitle: 'Master-planned Hills community at Mount Barker',
    overview: 'Larkview is a master-planned residential community at Mount Barker positioned for families wanting Hills lifestyle without sacrificing access to amenity. Releases include a mix of compact and family lots with reserves, play spaces and walking links woven through the masterplan.',
    estimatedLotSize: '350 - 700 m²',
    driveToCBD: '~35 mins',
    stage: 'Actively selling',
    estDelivery: 'Rolling releases 2024 - 2030',
    features: [
      'Mount Barker hills lifestyle and cooler climate',
      'Reserves and play space integrated into masterplan',
      'Mix of compact and family-sized lots',
      'Quick access to the Mount Barker town centre',
      'Quality schools within the broader catchment',
    ],
    considerations: [
      'Lot slope and orientation drive site cost variance',
      'Some lots fall within bushfire (BAL) overlays',
      'Confirm stage-specific design guidelines',
      'Peak-time freeway congestion is a factor for daily commuters',
    ],
    intentTags: ['House and land Mount Barker', 'Larkview estate', 'Adelaide Hills land for sale'],
    nearbySuburbs: ['Littlehampton', 'Nairne', 'Hahndorf'],
  },

  springlake: {
    slug: 'springlake',
    name: 'Springlake',
    suburb: 'Mount Barker',
    locality: 'Mount Barker',
    region: 'Adelaide Hills',
    state: 'SA',
    developer: 'Springlake Communities',
    metaTitle: 'Springlake Communities Mount Barker - Independent Buyer Guide | BuildPilot',
    metaDescription: 'Independent buyer guide to Springlake Communities at Mount Barker. Lake-side living, parkland, community amenity. BuildPilot One on One Support available.',
    heroEyebrow: 'Adelaide Hills · Master-planned',
    heroTitle: 'Springlake',
    heroSubtitle: 'Lake-and-parkland centred community at Mount Barker',
    overview: "Springlake Communities is a master-planned residential development at Mount Barker organised around a central lake and parkland system. It targets buyers who want a recreational, walkable estate environment with a Hills setting - popular with downsizers and family buyers alike.",
    estimatedLotSize: '350 - 800 m²',
    driveToCBD: '~35 mins',
    stage: 'Actively selling',
    estDelivery: 'Rolling releases 2024 - 2030',
    features: [
      'Lake-centred masterplan with surrounding parkland',
      'Walking and cycling trails integrated through the estate',
      'Strong family / downsizer appeal',
      'Quick access to Mount Barker amenity precincts',
      'Adelaide Hills micro-climate',
    ],
    considerations: [
      'Lake-frontage lots command a meaningful premium',
      'Some lots carry strict landscaping/design covenants',
      'Hills slope and BAL ratings vary by stage',
      'Confirm freeway commute timing in peak periods',
    ],
    intentTags: ['Springlake Mount Barker', 'Lake estate Adelaide Hills', 'House and land Mount Barker'],
    nearbySuburbs: ['Littlehampton', 'Nairne', 'Hahndorf'],
  },

  parkland: {
    slug: 'parkland',
    name: 'Parkland',
    suburb: 'Mount Barker',
    locality: 'Mount Barker',
    region: 'Adelaide Hills',
    state: 'SA',
    developer: 'Lanser',
    metaTitle: 'Parkland Estate Mount Barker - Independent Buyer Guide | BuildPilot',
    metaDescription: 'Independent buyer guide to Parkland at Mount Barker. Lot sizes, walkable parkland, Hills lifestyle. BuildPilot One on One Support available.',
    heroEyebrow: 'Adelaide Hills · Master-planned',
    heroTitle: 'Parkland',
    heroSubtitle: 'Parkland-rich Lanser community at Mount Barker',
    overview: 'Parkland is a Lanser-developed master-planned community at Mount Barker, branded around extensive green space, walking trails and family-friendly amenity. Stages have released steadily and the estate suits buyers wanting outdoor lifestyle on the city edge without leaving the Hills.',
    estimatedLotSize: '350 - 750 m²',
    driveToCBD: '~35 mins',
    stage: 'Actively selling',
    estDelivery: 'Rolling releases 2024 - 2030',
    features: [
      'Generous parkland and trail network',
      'Family-friendly amenity and play spaces',
      'Hills micro-climate and cooler summers',
      'Walkable to Mount Barker shopping precincts',
      'Strong owner-occupier mix',
    ],
    considerations: [
      'Site cost varies with slope and retaining requirements',
      'BAL overlays apply to some stages',
      'Design guidelines may restrict roof, façade and colour palette',
      'Peak-hourway congestion is a daily-commuter consideration',
    ],
    intentTags: ['Parkland estate Mount Barker', 'Adelaide Hills land', 'Mount Barker new home community'],
    nearbySuburbs: ['Littlehampton', 'Nairne', 'Hahndorf'],
  },

  'hampton-hill': {
    slug: 'hampton-hill',
    name: 'Hampton Hill',
    suburb: 'Mount Barker',
    locality: 'Mount Barker',
    region: 'Adelaide Hills',
    state: 'SA',
    developer: 'Hampton Hill Developments',
    metaTitle: 'Hampton Hill Estate Mount Barker - Independent Buyer Guide | BuildPilot',
    metaDescription: 'Independent buyer guide to Hampton Hill at Mount Barker. Family-orientated Hills community with parkland and lot variety. BuildPilot One on One Support available.',
    heroEyebrow: 'Adelaide Hills · Master-planned',
    heroTitle: 'Hampton Hill',
    heroSubtitle: 'Family-oriented Hills community at Mount Barker',
    overview: 'Hampton Hill is a family-focused master-planned community at Mount Barker. The masterplan delivers a mix of lot sizes, parkland reserves and community amenity, and it has become a popular choice for second-home buyers upgrading within the Hills.',
    estimatedLotSize: '350 - 700 m²',
    driveToCBD: '~35 mins',
    stage: 'Actively selling',
    estDelivery: 'Rolling releases 2024 - 2030',
    features: [
      'Family-focused masterplan',
      'Reserves and connected pedestrian links',
      'Mix of compact and family lots',
      'Quick access to Mount Barker town centre and schools',
      'Hills micro-climate and cooler summers',
    ],
    considerations: [
      'Some lots within BAL overlay zones - confirm bushfire requirements',
      'Slope can materially affect site cost',
      'Confirm stage-specific design guidelines',
      'Freeway commute can extend during peak-hour congestion',
    ],
    intentTags: ['Hampton Hill Mount Barker', 'Hills family estate Adelaide', 'House and land Mount Barker'],
    nearbySuburbs: ['Littlehampton', 'Nairne', 'Hahndorf'],
  },

  'clover-wood': {
    slug: 'clover-wood',
    name: 'Clover Wood',
    suburb: 'Munno Para',
    locality: 'Munno Para',
    region: 'Northern Adelaide',
    state: 'SA',
    developer: 'Statesman Homes Communities',
    metaTitle: 'Clover Wood Estate Munno Para - Independent Buyer Guide | BuildPilot',
    metaDescription: 'Independent buyer guide to Clover Wood at Munno Para. Northern Adelaide land, lot sizes, transit access. BuildPilot One on One Support available.',
    heroEyebrow: 'Northern Adelaide · Master-planned',
    heroTitle: 'Clover Wood',
    heroSubtitle: 'Northern Adelaide community at Munno Para',
    overview: 'Clover Wood is a master-planned community at Munno Para in Adelaide\'s growing northern corridor. Releases focus on titled lots, a mix of compact and family-sized blocks, and easy access to the train line and Northern Expressway.',
    estimatedLotSize: '300 - 600 m²',
    driveToCBD: '~35 mins',
    stage: 'Actively selling',
    estDelivery: 'Rolling releases 2024 - 2029',
    features: [
      'Titled lots - fast build-start possible',
      'Mix of compact and family-sized blocks',
      'Train access via the Munno Para station',
      'Access to Northern Expressway / Main North Road',
      'Established northern retail and amenities nearby',
    ],
    considerations: [
      'Mixed surrounding demographics - research specific streets',
      'Confirm lot-level site cost (cut/fill, retaining, services)',
      'Some lots have restrictive façade covenants',
      'Resale demographics vary by pocket - worth a careful look',
    ],
    intentTags: ['House and land Munno Para', 'Clover Wood estate', 'Northern Adelaide titled lots'],
    nearbySuburbs: ['Andrews Farm', 'Smithfield', 'Davoren Park'],
  },

  'senses-mount-barker': {
    slug: 'senses-mount-barker',
    name: 'SENSES Mount Barker',
    suburb: 'Mount Barker',
    locality: 'Mount Barker',
    region: 'Adelaide Hills',
    state: 'SA',
    developer: 'Bensons Property Group',
    metaTitle: 'SENSES Mount Barker - Independent Buyer Guide | BuildPilot',
    metaDescription: 'Independent buyer guide to SENSES at Mount Barker. Premium hills lifestyle, contemporary design guidelines and Hills amenity. BuildPilot One on One Support available.',
    heroEyebrow: 'Adelaide Hills · Premium master-planned',
    heroTitle: 'SENSES Mount Barker',
    heroSubtitle: 'Premium hills community by Bensons Property Group',
    overview: "SENSES is a premium master-planned community at Mount Barker positioned at the upper end of the Hills market. It is branded around contemporary architecture, considered landscape design and an active community programme - suited to buyers who place a premium on streetscape and design quality.",
    estimatedLotSize: '400 - 900 m²',
    driveToCBD: '~35 mins',
    stage: 'Actively selling',
    estDelivery: 'Rolling releases 2024 - 2032',
    features: [
      'Premium positioning within the Hills market',
      'Contemporary design guidelines and façade standards',
      'Considered landscape and street design',
      'Walking and trail network through the masterplan',
      'Adelaide Hills micro-climate',
    ],
    considerations: [
      'Premium land pricing reflects positioning',
      'Strict design guidelines materially affect builder selection',
      'Hills slope and BAL overlays apply on some lots',
      'Larger lots can mean longer landscaping completion timeframes',
    ],
    intentTags: ['SENSES Mount Barker', 'Premium Hills estate Adelaide', 'Bensons Mount Barker'],
    nearbySuburbs: ['Littlehampton', 'Nairne', 'Hahndorf'],
  },

  miravale: {
    slug: 'miravale',
    name: 'Miravale',
    suburb: 'Angle Vale',
    locality: 'Angle Vale',
    region: 'Northern Adelaide',
    state: 'SA',
    developer: 'Lanser',
    metaTitle: 'Miravale Estate Angle Vale - Independent Buyer Guide | BuildPilot',
    metaDescription: 'Independent buyer guide to Miravale at Angle Vale by Lanser. Lot sizes, parkland, release stages and what to weigh up before you commit. BuildPilot One on One Support available.',
    heroEyebrow: 'Northern Adelaide · Master-planned',
    heroTitle: 'Miravale',
    heroSubtitle: "Lanser's growing Angle Vale community",
    overview: 'Miravale is a Lanser master-planned community at Angle Vale in Adelaide\'s north, releasing steadily across multiple stages. It is pitched at first-home buyers and growing families, with a mix of compact and family-sized allotments, landscaped reserves and a planned village heart, roughly 40 minutes from the Adelaide CBD.',
    estimatedLotSize: '300 - 600 m²',
    driveToCBD: '~40 mins',
    stage: 'Actively selling',
    estDelivery: 'Rolling releases 2024 - 2032',
    features: [
      'Lanser master-planned community at Angle Vale',
      'Mix of courtyard and family-sized allotments',
      'Landscaped reserves, wetlands and walking trails',
      'Planned village and community facilities',
      'Close to Angle Vale township and schools',
      'Titled and registering allotments across stages',
    ],
    considerations: [
      'Angle Vale is still urbanising - some amenity is future-dated',
      'Public transport is limited; factor commute and a second vehicle',
      'Design covenants and landscaping timeframes vary by stage',
      'Site costs differ with lot orientation and fall',
      'Confirm school zoning for your target stage before committing',
    ],
    intentTags: ['House and land Angle Vale', 'Miravale Lanser', 'Northern Adelaide estates'],
    nearbySuburbs: ['Virginia', 'Gawler', 'Munno Para'],
  },

  'virginia-grove': {
    slug: 'virginia-grove',
    name: 'Virginia Grove',
    suburb: 'Virginia',
    locality: 'Virginia',
    region: 'Northern Adelaide',
    state: 'SA',
    developer: 'Lanser',
    metaTitle: 'Virginia Grove Estate Virginia - Independent Buyer Guide | BuildPilot',
    metaDescription: 'Independent buyer guide to Virginia Grove at Virginia by Lanser. Lot sizes, parkland, release stages and the trade-offs to weigh up. BuildPilot One on One Support available.',
    heroEyebrow: 'Northern Adelaide · Master-planned',
    heroTitle: 'Virginia Grove',
    heroSubtitle: "Lanser's established community at Virginia",
    overview: 'Virginia Grove is a long-running Lanser community at Virginia in Adelaide\'s north, surrounded by the Northern Adelaide Plains market gardens. It offers affordable family-sized allotments, reserves and walking trails in a semi-rural setting about 40 minutes from the Adelaide CBD.',
    estimatedLotSize: '350 - 700 m²',
    driveToCBD: '~40 mins',
    stage: 'Actively selling',
    estDelivery: 'Rolling releases 2024 - 2030',
    features: [
      'Lanser master-planned community at Virginia',
      'Affordable family-sized allotments',
      'Parkland reserves and walking trails',
      'Semi-rural Northern Adelaide Plains setting',
      'Close to Virginia township and Northern Expressway',
      'Titled allotments available',
    ],
    considerations: [
      'Semi-rural location - amenity is spread out',
      'Public transport is limited in the area',
      'Market-garden surrounds can mean seasonal traffic and spray activity',
      'Confirm soil classification and site costs per lot',
      'Design covenants vary across releases',
    ],
    intentTags: ['House and land Virginia', 'Virginia Grove Lanser', 'Northern Adelaide estates'],
    nearbySuburbs: ['Angle Vale', 'Two Wells', 'Munno Para'],
  },

  'liberty-two-wells': {
    slug: 'liberty-two-wells',
    name: 'Liberty',
    suburb: 'Two Wells',
    locality: 'Two Wells',
    region: 'Northern Adelaide',
    state: 'SA',
    developer: 'Hickinbotham',
    metaTitle: 'Liberty Estate Two Wells - Independent Buyer Guide | BuildPilot',
    metaDescription: 'Independent buyer guide to Liberty at Two Wells by Hickinbotham. Lot sizes, masterplan, release stages and what to weigh up before you commit. BuildPilot One on One Support available.',
    heroEyebrow: 'Northern Adelaide · Master-planned',
    heroTitle: 'Liberty',
    heroSubtitle: "Hickinbotham's affordable Two Wells community",
    overview: 'Liberty is a Hickinbotham master-planned community at Two Wells, one of Adelaide\'s more affordable northern growth fronts. It targets first-home buyers and young families with value-focused allotments, reserves and a planned community heart, roughly 45 minutes north of the Adelaide CBD.',
    estimatedLotSize: '300 - 650 m²',
    driveToCBD: '~45 mins',
    stage: 'Actively selling',
    estDelivery: 'Rolling releases 2024 - 2032',
    features: [
      'Hickinbotham master-planned community at Two Wells',
      'Value-focused allotments for first-home buyers',
      'Landscaped reserves and open space',
      'Planned community and retail heart',
      'Close to the Two Wells township',
      'Titled and registering allotments',
    ],
    considerations: [
      'One of the furthest northern growth fronts - factor commute time',
      'Public transport is limited; a second vehicle is often needed',
      'Local amenity is still building out',
      'Site costs vary with lot fall and soil',
      'Design covenants differ between releases',
    ],
    intentTags: ['House and land Two Wells', 'Liberty Hickinbotham', 'Affordable Northern Adelaide estates'],
    nearbySuburbs: ['Virginia', 'Angle Vale', 'Mallala'],
  },

  'playford-alive': {
    slug: 'playford-alive',
    name: 'Playford Alive',
    suburb: 'Munno Para',
    locality: 'Munno Para / Smithfield',
    region: 'Northern Adelaide',
    state: 'SA',
    developer: 'Renewal SA',
    metaTitle: 'Playford Alive Munno Para - Independent Buyer Guide | BuildPilot',
    metaDescription: 'Independent buyer guide to Playford Alive at Munno Para / Smithfield, a Renewal SA urban renewal community. Lot sizes, amenity and what to weigh up. BuildPilot One on One Support available.',
    heroEyebrow: 'Northern Adelaide · Urban renewal',
    heroTitle: 'Playford Alive',
    heroSubtitle: 'Renewal SA urban-renewal community at Munno Para',
    overview: 'Playford Alive is a major Renewal SA urban-renewal community across Munno Para and Smithfield in Adelaide\'s north, delivered with development partners over many years. It combines affordable new allotments with established rail, schools, a town centre and extensive parkland, about 35 minutes from the Adelaide CBD.',
    estimatedLotSize: '300 - 550 m²',
    driveToCBD: '~35 mins',
    stage: 'Actively selling',
    estDelivery: 'Long-horizon renewal (2024 - 2030+)',
    features: [
      'Renewal SA urban-renewal community',
      'Affordable allotments for first-home buyers',
      'Established Munno Para rail and bus services',
      'Town centre, schools and parklands nearby',
      'Extensive open space and community facilities',
      'Mix of titled allotments',
    ],
    considerations: [
      'Renewal area with a mix of new and established housing',
      'Allotments and covenants vary by precinct',
      'Check the specific precinct\'s amenity and stage of delivery',
      'Site costs vary lot to lot',
      'Confirm school zoning for your target precinct',
    ],
    intentTags: ['House and land Munno Para', 'Playford Alive', 'Affordable Northern Adelaide estates'],
    nearbySuburbs: ['Smithfield', 'Elizabeth', 'Andrews Farm'],
  },

  'roseworthy-garden-estate': {
    slug: 'roseworthy-garden-estate',
    name: 'Roseworthy Garden Estate',
    suburb: 'Roseworthy',
    locality: 'Roseworthy',
    region: 'Northern Adelaide',
    state: 'SA',
    developer: 'Land Australia',
    metaTitle: 'Roseworthy Garden Estate - Independent Buyer Guide | BuildPilot',
    metaDescription: 'Independent buyer guide to Roseworthy Garden Estate at Roseworthy by Land Australia. Lot sizes, masterplan precincts and what to weigh up. BuildPilot One on One Support available.',
    heroEyebrow: 'Northern Adelaide · Master-planned',
    heroTitle: 'Roseworthy Garden Estate',
    heroSubtitle: "Land Australia's community at Roseworthy, north of Gawler",
    overview: 'Roseworthy Garden Estate is a Land Australia master-planned community at Roseworthy, just north of Gawler, releasing across precincts such as Evergreen and Flourish. It offers larger, value-oriented family allotments in a country-edge setting, roughly 50 minutes from the Adelaide CBD.',
    estimatedLotSize: '375 - 800 m²',
    driveToCBD: '~50 mins',
    stage: 'Actively selling',
    estDelivery: 'Rolling releases 2024 - 2032',
    features: [
      'Land Australia master-planned community',
      'Larger, value-oriented family allotments',
      'Evergreen and Flourish precincts',
      'Open space and community reserves',
      'Country-edge setting north of Gawler',
      'Titled and registering allotments',
    ],
    considerations: [
      'Furthest northern growth front - commute time is significant',
      'Minimal public transport; plan for multiple vehicles',
      'Local amenity is limited and still developing',
      'Site and fencing costs vary with larger lots',
      'Design covenants differ between precincts',
    ],
    intentTags: ['House and land Roseworthy', 'Roseworthy Garden Estate', 'Gawler region estates'],
    nearbySuburbs: ['Gawler', 'Freeling', 'Evanston'],
  },

  'aston-hills': {
    slug: 'aston-hills',
    name: 'Aston Hills',
    suburb: 'Mount Barker',
    locality: 'Mount Barker',
    region: 'Adelaide Hills',
    state: 'SA',
    developer: 'Burke Urban',
    metaTitle: 'Aston Hills Estate Mount Barker - Independent Buyer Guide | BuildPilot',
    metaDescription: 'Independent buyer guide to Aston Hills at Mount Barker by Burke Urban. Lot sizes, parkland, release stages and the Hills trade-offs. BuildPilot One on One Support available.',
    heroEyebrow: 'Adelaide Hills · Master-planned',
    heroTitle: 'Aston Hills',
    heroSubtitle: "Burke Urban's established Mount Barker community",
    overview: 'Aston Hills is a Burke Urban master-planned community at Mount Barker, one of the Adelaide Hills\' fastest-growing towns. It is known for its parkland network, village green and a broad range of allotments, with established schools and the Mount Barker town centre close by, about 35 minutes from the CBD.',
    estimatedLotSize: '300 - 700 m²',
    driveToCBD: '~35 mins',
    stage: 'Actively selling',
    estDelivery: 'Rolling releases 2024 - 2030',
    features: [
      'Burke Urban master-planned Mount Barker community',
      'Extensive parkland network and village green',
      'Broad range of allotment sizes',
      'Close to Mount Barker town centre and schools',
      'Established community with active display homes',
      'Titled allotments available',
    ],
    considerations: [
      'Hills slope and some BAL overlays apply on parts of the estate',
      'Winter weather and fog affect the Hills commute',
      'Design covenants influence facade and landscaping',
      'Site costs vary noticeably with slope',
      'Confirm connection timing for services on newer lots',
    ],
    intentTags: ['House and land Mount Barker', 'Aston Hills Burke Urban', 'Adelaide Hills estates'],
    nearbySuburbs: ['Littlehampton', 'Nairne', 'Hahndorf'],
  },

  'minters-rise': {
    slug: 'minters-rise',
    name: 'Minters Rise',
    suburb: 'Mount Barker',
    locality: 'Mount Barker',
    region: 'Adelaide Hills',
    state: 'SA',
    developer: 'Metro Homes SA',
    metaTitle: 'Minters Rise Estate Mount Barker - Independent Buyer Guide | BuildPilot',
    metaDescription: 'Independent buyer guide to Minters Rise at Mount Barker by Metro Homes SA. Lot sizes, release stages and the Hills considerations. BuildPilot One on One Support available.',
    heroEyebrow: 'Adelaide Hills · Master-planned',
    heroTitle: 'Minters Rise',
    heroSubtitle: "Metro Homes SA's Mount Barker community",
    overview: 'Minters Rise is a Metro Homes SA community at Mount Barker, releasing family-sized allotments on the edge of the growing Hills town. It suits buyers who want a Hills lifestyle within reach of the Mount Barker town centre, schools and the South Eastern Freeway, around 35 minutes from the CBD.',
    estimatedLotSize: '350 - 700 m²',
    driveToCBD: '~35 mins',
    stage: 'Actively selling',
    estDelivery: 'Rolling releases 2024 - 2030',
    features: [
      'Metro Homes SA master-planned community',
      'Family-sized Hills allotments',
      'Open space and reserves',
      'Close to Mount Barker town centre and schools',
      'Freeway access to the CBD',
      'Titled and registering allotments',
    ],
    considerations: [
      'Hills slope affects some lots and site costs',
      'Winter fog and weather affect the commute',
      'Design covenants apply across releases',
      'Confirm service connection timing on new lots',
      'Check BAL overlay status for your target lot',
    ],
    intentTags: ['House and land Mount Barker', 'Minters Rise', 'Adelaide Hills estates'],
    nearbySuburbs: ['Littlehampton', 'Nairne', 'Totness'],
  },

  'adore-mount-barker': {
    slug: 'adore-mount-barker',
    name: 'Adore',
    suburb: 'Mount Barker',
    locality: 'Mount Barker',
    region: 'Adelaide Hills',
    state: 'SA',
    developer: 'Ciampa Developments',
    metaTitle: 'Adore Estate Mount Barker - Independent Buyer Guide | BuildPilot',
    metaDescription: 'Independent buyer guide to Adore at Mount Barker by Ciampa Developments. Lot sizes, linear parkland and the Hills considerations. BuildPilot One on One Support available.',
    heroEyebrow: 'Adelaide Hills · Master-planned',
    heroTitle: 'Adore',
    heroSubtitle: 'A Ciampa Developments community at Mount Barker',
    overview: 'Adore is a Ciampa Developments community at Mount Barker built around a linear park and walking network. It offers a range of allotments in a maturing Hills town setting, close to schools, the town centre and freeway access, roughly 35 minutes from the Adelaide CBD.',
    estimatedLotSize: '300 - 650 m²',
    driveToCBD: '~35 mins',
    stage: 'Actively selling',
    estDelivery: 'Rolling releases 2024 - 2030',
    features: [
      'Ciampa Developments community at Mount Barker',
      'Linear park and walking network',
      'Range of allotment sizes',
      'Close to town centre, schools and freeway',
      'Maturing Adelaide Hills location',
      'Titled allotments available',
    ],
    considerations: [
      'Hills slope affects some lots and landscaping',
      'Winter weather affects the Hills commute',
      'Design guidelines shape facade and fencing',
      'Site costs vary with slope and soil',
      'Confirm the current release and its amenity timing',
    ],
    intentTags: ['House and land Mount Barker', 'Adore Mount Barker', 'Adelaide Hills estates'],
    nearbySuburbs: ['Littlehampton', 'Nairne', 'Totness'],
  },

  aldinga: {
    slug: 'aldinga',
    name: 'Aldinga',
    suburb: 'Aldinga',
    locality: 'Aldinga',
    region: 'Southern Adelaide',
    state: 'SA',
    developer: 'Renewal SA & Villawood Properties',
    metaTitle: 'Aldinga Net-Zero Community - Independent Buyer Guide | BuildPilot',
    metaDescription: 'Independent buyer guide to the Aldinga net-zero community, a Renewal SA and Villawood Properties project in southern Adelaide. What to weigh up before you commit. BuildPilot One on One Support available.',
    heroEyebrow: 'Southern Adelaide · Master-planned',
    heroTitle: 'Aldinga',
    heroSubtitle: 'A Renewal SA & Villawood net-zero coastal community',
    overview: 'Aldinga is a new net-zero master-planned community in southern Adelaide, delivered as a partnership between Renewal SA and Villawood Properties and planned for around 800 homes. Set near the Aldinga coast and township, it is focused on sustainable, all-electric living, with civil works progressing and sales expected to open from 2026.',
    estimatedLotSize: '300 - 600 m²',
    driveToCBD: '~50 mins',
    stage: 'Launching 2026',
    estDelivery: 'Civil works underway · sales from 2026',
    features: [
      'Renewal SA and Villawood Properties partnership',
      'Net-zero, all-electric sustainability focus',
      'Planned for around 800 homes',
      'Near the Aldinga coast and township',
      'Planned open space and community facilities',
      'Southern Expressway access to the CBD',
    ],
    considerations: [
      'Sales and titling are early-stage - timing can move',
      'Sustainability requirements may shape build specifications',
      'Furthest southern coastal front - factor commute time',
      'Confirm the current release, pricing and timing directly',
      'Public transport is limited in the area',
    ],
    intentTags: ['House and land Aldinga', 'Aldinga net zero community', 'Southern Adelaide estates'],
    nearbySuburbs: ['Seaford', 'Willunga', 'Sellicks Beach'],
  },

  'burgundy-estate': {
    slug: 'burgundy-estate',
    name: 'Burgundy Estate',
    suburb: 'Angle Vale',
    locality: 'Angle Vale',
    region: 'Northern Adelaide',
    state: 'SA',
    developer: 'Century 21 Special Projects (SA)',
    metaTitle: 'Burgundy Estate Angle Vale - Independent Buyer Guide | BuildPilot',
    metaDescription: 'Independent buyer guide to Burgundy Estate at Angle Vale in Adelaide\'s north. Lot sizes, release stages, registrations and what to weigh up before you commit. BuildPilot One on One Support available.',
    heroEyebrow: 'Northern Adelaide · Master-planned',
    heroTitle: 'Burgundy Estate',
    heroSubtitle: 'A growing Angle Vale land release',
    overview: 'Burgundy Estate is an actively selling land release at Angle Vale in Adelaide\'s northern growth corridor, rolling out across multiple stages. It offers a broad spread of allotment sizes suited to first-home buyers, families and downsizers, roughly 40 minutes from the Adelaide CBD and close to the Angle Vale township.',
    estimatedLotSize: '300 - 800 m²',
    driveToCBD: '~40 mins',
    stage: 'Actively selling',
    estDelivery: 'Rolling releases across stages',
    features: [
      'Actively selling land release at Angle Vale',
      'Wide spread of allotment sizes (300 - 800 m²)',
      'Multiple stages with registrations taken for upcoming releases',
      'Close to Angle Vale township, shops and schools',
      'Part of Adelaide\'s fast-growing northern corridor',
      'Titled and registering allotments across stages',
    ],
    considerations: [
      'Angle Vale is still urbanising - some amenity is future-dated',
      'Public transport is limited; factor commute and a second vehicle',
      'Lot availability changes quickly - confirm current stock before committing',
      'Design covenants and site costs vary by stage and lot fall',
      'Confirm school zoning for your target stage',
    ],
    intentTags: ['House and land Angle Vale', 'Burgundy Estate Angle Vale', 'Northern Adelaide estates'],
    nearbySuburbs: ['Virginia', 'Gawler', 'Munno Para'],
  },
};

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------

export const getEstateData = (slug) => estateData[slug] || null;
export const getAllEstates = () => Object.values(estateData);
export const getEstatesByRegion = (region) =>
  Object.values(estateData).filter((e) => e.region === region);
export const getEstateSlugs = () => Object.keys(estateData);
export const getEstateRegions = () => Array.from(new Set(Object.values(estateData).map((e) => e.region)));
